import type { VisualSettings } from './adminConfig';
import { Workspace, CreateWorkspaceRequest } from '@/types';
import { api } from './client';

/**
 * Composite workspace settings — one endpoint pair (`GET`/`PATCH
 * /workspaces/:id/settings`) that replaced the former per-field RPC fan
 * (`/settings/{cadence-mode,linkedin-publish-mode,approval-notifications,
 * showcase}`). `linkedinPublishMode` is read-only (mirrors a global system
 * setting). The single-field helpers below delegate here for back-compat.
 */
export interface WorkspaceSettings {
  cadenceMode: 'WEEKLY' | 'DAILY';
  linkedinPublishMode: string;
  approvalNotifyEmails: string[];
  showcaseOptIn: boolean;
}

export interface UpdateWorkspaceSettings {
  cadenceMode?: 'WEEKLY' | 'DAILY';
  approvalNotifyEmails?: string[];
  showcaseOptIn?: boolean;
}

export const workspaceApi = {
  list: () => api.get<Workspace[]>('/api/workspaces').then((r) => r.data),
  create: (data: CreateWorkspaceRequest) =>
    api.post<Workspace>('/api/workspaces', data).then((r) => r.data),
  get: (id: string) => api.get<Workspace>(`/api/workspaces/${id}`).then((r) => r.data),
  getVisualSettings: (id: string) =>
    api.get<VisualSettings>(`/api/workspaces/${id}/visual-settings`).then((r) => r.data),
  updateVisualSettings: (
    id: string,
    data: { model?: string; imageSize?: string; numImages?: number },
  ) => api.patch<VisualSettings>(`/api/workspaces/${id}/visual-settings`, data).then((r) => r.data),
  // ── Consolidated settings (composite GET/PATCH) ──────────────────────────
  getSettings: (id: string) =>
    api.get<WorkspaceSettings>(`/api/workspaces/${id}/settings`).then((r) => r.data),
  updateSettings: (id: string, patch: UpdateWorkspaceSettings) =>
    api.patch<WorkspaceSettings>(`/api/workspaces/${id}/settings`, patch).then((r) => r.data),

  // V109 — workspace-level cadence generation mode (WEEKLY or DAILY).
  // WEEKLY: full batch every Monday 06:00 (legacy behavior). DAILY: one
  // post per cadence on each preferred day at 06:00. Owner-only on PATCH.
  // Thin wrappers over the composite endpoint — kept so existing components
  // don't churn; new code can call getSettings/updateSettings directly.
  getCadenceMode: (id: string) =>
    workspaceApi.getSettings(id).then((s) => ({ mode: s.cadenceMode })),
  setCadenceMode: (id: string, mode: 'WEEKLY' | 'DAILY') =>
    workspaceApi.updateSettings(id, { cadenceMode: mode }).then((s) => ({ mode: s.cadenceMode })),
  // V129 — approval-notification recipient list. Empty list = fall
  // back to workspace owner email at notification-resolve time.
  getApprovalNotifications: (id: string) =>
    workspaceApi.getSettings(id).then((s) => ({ emails: s.approvalNotifyEmails })),
  setApprovalNotifications: (id: string, emails: string[]) =>
    workspaceApi
      .updateSettings(id, { approvalNotifyEmails: emails })
      .then((s) => ({ emails: s.approvalNotifyEmails })),
  // V161 — public marketing showcase opt-in. GET open to members, PATCH
  // owner-only on the backend.
  getShowcaseOptIn: (id: string) =>
    workspaceApi.getSettings(id).then((s) => ({ optIn: s.showcaseOptIn })),
  setShowcaseOptIn: (id: string, optIn: boolean) =>
    workspaceApi.updateSettings(id, { showcaseOptIn: optIn }).then((s) => ({
      optIn: s.showcaseOptIn,
    })),
  /**
   * Whether the workspace can actually deliver notification emails
   * today. `source` is `workspace` (own SMTP), `global` (admin SMTP),
   * or `none`. Drives the approval-notification settings warning
   * banner — when `canSend` is false, the recipient list is config'd
   * but no email will reach them.
   */
  getNotificationDiagnostics: (id: string) =>
    api
      .get<{
        canSend: boolean;
        source: 'workspace' | 'global' | 'none';
      }>(`/api/workspaces/${id}/settings/notifications/diagnostics`)
      .then((r) => r.data),
};
